/**
 * 나라장터 다량납품할인 기준 (v2.315.0 · 2026-10-30 · 사용자 「다량납품 할인이 항목마다 적용 요율이 달라 ·
 *   각 항목별로 다량납품 할인 기준을 항목 속성에 추가해서 자동으로 할인율이 반영될 수 있도록」).
 *
 * 정산요율 항목(나라장터 › 정산요율 탭의 한 줄)마다 기준을 둔다 — 서버 칸 g2b_settlement_rate.volume_discount(seq_0046).
 *   basis 'amount' = 정산서 그 줄의 금액(계약단가 × 수량)이 min 원 이상 · 'qty' = 그 줄 수량이 min 개 이상
 *   → 맞는 구간 중 min 이 가장 큰 것의 pct(%). 정산서 작성 창이 줄을 넣을 때·수량/단가를 바꿀 때 이 값으로 채운다.
 * 검사: `npm run check:settlement-won` ⑤.
 */

export type VolumeDiscountBasis = 'amount' | 'qty';
export interface VolumeDiscountTier {
  /** 기준 — 금액(원) 또는 수량(개) 이상 */
  min: number;
  /** 할인율(%) — 0.5 = 0.5% */
  pct: number;
}
export interface VolumeDiscount {
  basis: VolumeDiscountBasis;
  tiers: VolumeDiscountTier[];
}

export const BASIS_LABEL: Readonly<Record<VolumeDiscountBasis, string>> = {
  amount: '금액 기준 (그 줄 계약단가 × 수량)',
  qty: '수량 기준 (그 줄 수량)',
};

/**
 * 정산서 한 줄에 맞는 다량납품할인율(%) — 기준이 없거나 어느 구간에도 못 미치면 null.
 *   amount = 그 줄 계약단가 × 수량(원) · qty = 그 줄 수량(개). 같은 기준이 「이상」이면 맞는다(경계 포함).
 */
export function autoDiscountPct(vd: VolumeDiscount | null | undefined, line: { amount: number; qty: number }): number | null {
  if (!vd || !Array.isArray(vd.tiers) || vd.tiers.length === 0) return null;
  const value = vd.basis === 'qty' ? line.qty : line.amount;
  if (!Number.isFinite(value)) return null;
  let best: VolumeDiscountTier | null = null;
  for (const t of vd.tiers) {
    if (!(t.pct > 0) || !(value >= t.min)) continue;
    if (!best || t.min > best.min) best = t;
  }
  return best ? best.pct : null;
}

/** 금액을 짧게 — 50,000,000 → 「5,000만」 · 120,000,000 → 「1억 2,000만」 · 9,900 → 「9,900」 */
export function shortWon(n: number): string {
  if (!Number.isFinite(n)) return String(n);
  const eok = Math.floor(n / 100_000_000);
  const man = Math.floor((n % 100_000_000) / 10_000);
  const rest = n % 10_000;
  if (n < 10_000 || rest !== 0) return n.toLocaleString('ko-KR');
  return [eok ? `${eok.toLocaleString('ko-KR')}억` : '', man ? `${man.toLocaleString('ko-KR')}만` : ''].filter(Boolean).join(' ');
}

/** 화면 글 — 「5,000만 원 이상 0.5% · 1억 원 이상 1%」 / 「10개 이상 1%」 · 기준이 없으면 '' */
export function volumeDiscountLabel(vd: VolumeDiscount | null | undefined): string {
  if (!vd || !Array.isArray(vd.tiers) || vd.tiers.length === 0) return '';
  const unit = vd.basis === 'qty' ? '개' : ' 원';
  return [...vd.tiers]
    .sort((a, b) => a.min - b.min)
    .map((t) => `${vd.basis === 'qty' ? t.min.toLocaleString('ko-KR') : shortWon(t.min)}${unit} 이상 ${t.pct}%`)
    .join(' · ');
}

/** 수정 창의 글자 칸들 → 저장할 기준(빈 줄은 뺀다) · 하나도 없으면 null · 숫자가 아니면 오류 문구 */
export function tiersFromText(basis: VolumeDiscountBasis, rows: readonly { min: string; pct: string }[]): { value: VolumeDiscount | null } | { error: string } {
  const tiers: VolumeDiscountTier[] = [];
  for (const [i, r] of rows.entries()) {
    const minT = r.min.replace(/[,\s]/g, '');
    const pctT = r.pct.trim();
    if (minT === '' && pctT === '') continue;
    const min = Number(minT);
    const pct = Number(pctT);
    if (minT === '' || !Number.isFinite(min) || min < 0) return { error: `${i + 1}번째 구간의 기준(${basis === 'qty' ? '개' : '원'})을 숫자로 적으세요` };
    if (basis === 'qty' && !Number.isInteger(min)) return { error: `${i + 1}번째 구간 — 수량 기준은 정수(개)로 적으세요` };
    if (pctT === '' || !Number.isFinite(pct) || pct <= 0 || pct >= 100) return { error: `${i + 1}번째 구간의 할인율(%)을 0 보다 크고 100 보다 작게 적으세요` };
    tiers.push({ min, pct });
  }
  if (tiers.length === 0) return { value: null };
  tiers.sort((a, b) => a.min - b.min);
  for (let i = 1; i < tiers.length; i++) {
    if (tiers[i]!.min === tiers[i - 1]!.min) return { error: `같은 기준(${tiers[i]!.min.toLocaleString('ko-KR')})이 두 번 있습니다` };
  }
  if (tiers.length > 10) return { error: '할인 구간은 10개까지입니다' };
  return { value: { basis, tiers } };
}
